import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { Sphere } from '@/components/Sphere';
import { Logo } from '@/components/Logo';
import { Stepper } from '@/components/Stepper';
import { SegmentedControl } from '@/components/SegmentedControl';
import { LangToggle } from '@/components/LangToggle';
import { CalendarExport } from '@/components/CalendarExport';
import { api, ApiError } from '@/lib/api';
import { formatDate, formatDeadline } from '@/lib/format';
import { googleMapsUrl } from '@/lib/calendar';
import { useLang, localeFor, type StringKey } from '@/i18n';
import type { PublicEventResponse, RsvpStatus } from '@shared/types';
import styles from './PublicEvent.module.css';

type State =
  | { kind: 'loading' }
  | { kind: 'not_found' }
  | { kind: 'error' }
  | { kind: 'event'; data: PublicEventResponse };

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'done'; persons: number; rsvp: RsvpStatus }
  | { kind: 'error'; message: string };

export function PublicEvent() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useLang();
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setState({ kind: 'loading' });
    api.getPublicEvent(slug).then(
      (data) => { if (!cancelled) setState({ kind: 'event', data }); },
      (err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) setState({ kind: 'not_found' });
        else setState({ kind: 'error' });
      },
    );
    return () => { cancelled = true; };
  }, [slug]);

  return (
    <div className={`brand-scale ${styles.shell}`}>
      <Sphere />
      <header className={styles.brand}>
        <Logo height={36} />
        <LangToggle />
      </header>
      <main className={styles.main}>
        {state.kind === 'loading' && <p className={styles.loading}>{t('loading')}</p>}
        {state.kind === 'not_found' && <NotFoundBlock />}
        {state.kind === 'error' && <ErrorBlock />}
        {state.kind === 'event' && <EventBlock data={state.data} slug={slug!} />}
      </main>
    </div>
  );
}

function NotFoundBlock() {
  const { t } = useLang();
  return (
    <section className={styles.message}>
      <h1 className={styles.h1}>{t('not_found_title')}</h1>
      <p className={styles.lead}>{t('not_found_body')}</p>
    </section>
  );
}

function ErrorBlock() {
  const { t } = useLang();
  return (
    <section className={styles.message}>
      <h1 className={styles.h1}>{t('error_title')}</h1>
      <p className={styles.lead}>{t('error_body')}</p>
    </section>
  );
}

function EventBlock({ data, slug }: { data: PublicEventResponse; slug: string }) {
  const { t, lang } = useLang();
  const { event, status, closedReason } = data;
  if (status === 'closed') {
    return (
      <section className={styles.message}>
        <p className={styles.eyebrow}>{t('eyebrow_closed')}</p>
        <h1 className={styles.h1}>{event.name}</h1>
        {event.subheading && <p className={styles.subheading}>{event.subheading}</p>}
        <p className={styles.lead}>
          {closedReason === 'deadline' ? (
            <>
              {t('closed_deadline_text')}<br />
              ({t('closed_deadline_when', { when: formatDeadline(event.registrationDeadline, localeFor(lang)) })})
            </>
          ) : (
            <>{t('closed_manual')}</>
          )}
        </p>
      </section>
    );
  }
  return <RegisterForm slug={slug} event={event} />;
}

function RegisterForm({ slug, event }: { slug: string; event: PublicEventResponse['event'] }) {
  const { t, lang } = useLang();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [extraGuests, setExtraGuests] = useState(0);
  const [rsvp, setRsvp] = useState<RsvpStatus | null>(null);
  const [submit, setSubmit] = useState<SubmitState>({ kind: 'idle' });

  const formValid = firstName.trim() !== '' && lastName.trim() !== '' && email.trim() !== '' && rsvp !== null;

  // Wenn Begleitungen serverseitig ausgeschaltet sind, ignorieren wir lokalen
  // Stepper-State komplett — der Stepper wird unten ohnehin nicht gerendert,
  // aber so kommen auch keine "Geister"-Werte ins Submit, falls jemand vor dem
  // Toggle-Wechsel schon hochgezählt hatte.
  const effectiveExtraGuests = event.allowGuests ? extraGuests : 0;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submit.kind === 'submitting' || !formValid || !rsvp) return;
    setSubmit({ kind: 'submitting' });
    try {
      const res = await api.register(slug, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        extraGuests: effectiveExtraGuests,
        rsvpStatus: rsvp,
      });
      setSubmit({ kind: 'done', persons: res.totalPersons, rsvp });
    } catch (err) {
      setSubmit({ kind: 'error', message: errorMessage(err, t) });
    }
  };

  if (submit.kind === 'done') {
    return (
      <section className={styles.message}>
        <p className={styles.eyebrow}>{t('eyebrow_done')}</p>
        <h1 className={styles.h1}>{t('done_title', { firstName })}</h1>
        <p className={styles.lead}>
          {submit.persons === 1
            ? t('done_body_one', {
                event: event.name,
                date: formatDate(event.eventDate, localeFor(lang)),
                time: event.eventTime,
              })
            : t('done_body_many', {
                persons: submit.persons,
                event: event.name,
                date: formatDate(event.eventDate, localeFor(lang)),
                time: event.eventTime,
              })}
        </p>
        <CalendarExport
          event={{
            name: event.name,
            eventDate: event.eventDate,
            eventTime: event.eventTime,
            location: event.location,
            subheading: event.subheading,
          }}
        />
      </section>
    );
  }

  const submitting = submit.kind === 'submitting';
  const summary = effectiveExtraGuests === 0
    ? t('summary_alone')
    : effectiveExtraGuests === 1
      ? t('summary_with_one')
      : t('summary_with_many', { n: effectiveExtraGuests, total: 1 + effectiveExtraGuests });

  // "Ich komme…" wenn allein, "Wir kommen…" sobald Begleitungen dazu kommen.
  const rsvpLabel = effectiveExtraGuests === 0 ? t('rsvp_question_solo') : t('rsvp_question_group');

  return (
    <section className={styles.formSection}>
      <header className={styles.headline}>
        <p className={styles.eyebrow}>{t('eyebrow_register')}</p>
        <h1 className={styles.h1}>{event.name}</h1>
        {event.subheading && <p className={styles.subheading}>{event.subheading}</p>}
        <p className={styles.lead}>
          {formatDate(event.eventDate, localeFor(lang))} &middot; {event.eventTime}{lang === 'de' ? ' Uhr' : ''}
          {event.moreInfoUrl && (
            <>
              {' '}&middot;{' '}
              <a
                className={styles.moreInfoLink}
                href={event.moreInfoUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('more_info_link')}
              </a>
            </>
          )}
        </p>
        {event.location && <LocationLine location={event.location} t={t} />}
      </header>

      <form className={styles.form} onSubmit={onSubmit} noValidate>
        <Field label={t('field_first_name')}>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            autoComplete="given-name"
            disabled={submitting}
          />
        </Field>
        <Field label={t('field_last_name')}>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            autoComplete="family-name"
            disabled={submitting}
          />
        </Field>
        <Field label={t('field_email')}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            inputMode="email"
            disabled={submitting}
          />
        </Field>

        {event.allowGuests && (
          <div className={styles.stepperRow}>
            <Stepper
              label={t('field_extra_guests')}
              value={extraGuests}
              min={0}
              max={10}
              onChange={setExtraGuests}
            />
          </div>
        )}

        {event.allowGuests && <p className={styles.summary}>{summary}</p>}

        <div className={styles.rsvpRow}>
          <SegmentedControl<RsvpStatus>
            label={rsvpLabel}
            value={rsvp}
            onChange={setRsvp}
            options={[
              { value: 'confirmed', label: t('rsvp_confirmed') },
              { value: 'tentative', label: t('rsvp_tentative') },
            ]}
            disabled={submitting}
          />

          {submit.kind === 'error' && (
            <p className={styles.error}>{submit.message}</p>
          )}

          <button type="submit" className={styles.submit} disabled={submitting || !formValid}>
            {submitting ? t('submit_busy') : t('submit_idle')}
          </button>

          <PrivacyNotice />
        </div>
      </form>
    </section>
  );
}

function PrivacyNotice() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);

  // ESC schließt — und body-scroll lock, damit Hintergrund nicht hinterm
  // Overlay weiterwandert.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        className={styles.privacyLink}
        onClick={() => setOpen(true)}
      >
        {t('privacy_link')}
      </button>
      {open && (
        <div
          className={styles.privacyOverlay}
          onClick={() => setOpen(false)}
          role="presentation"
        >
          <div
            className={styles.privacyModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="privacy-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={styles.privacyClose}
              onClick={() => setOpen(false)}
              aria-label={t('privacy_close_aria')}
            >
              <i className="ph ph-x" aria-hidden="true" />
            </button>
            <h2 id="privacy-title" className={styles.privacyTitle}>
              {t('privacy_title')}
            </h2>
            <p className={styles.privacyBody}>{t('privacy_body')}</p>
          </div>
        </div>
      )}
    </>
  );
}

function LocationLine({ location, t }: { location: string; t: (k: StringKey) => string }) {
  // Immer auf Google Maps verlinken — auch reine Location-Namen wie
  // "TRO Studio Düsseldorf" findet Maps zuverlässig.
  return (
    <a
      className={styles.locationLink}
      href={googleMapsUrl(location)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t('location_open_maps_aria')}
    >
      <i className="ph ph-map-pin" aria-hidden="true" />
      <span>{location}</span>
    </a>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

function errorMessage(err: unknown, t: (k: StringKey) => string): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'email_already_registered': return t('err_email_taken');
      case 'invalid_email': return t('err_invalid_email');
      case 'invalid_first_name': return t('err_invalid_first_name');
      case 'invalid_last_name': return t('err_invalid_last_name');
      case 'invalid_extra_guests': return t('err_invalid_extras');
      case 'invalid_rsvp_status': return t('err_invalid_rsvp');
      case 'guests_not_allowed': return t('err_guests_not_allowed');
      case 'closed': return t('err_closed');
      case 'not_found': return t('err_not_found');
    }
  }
  return t('err_generic');
}
